// src/hooks/useCompanyPlan.js
// Replace your existing useCompanyPlan.js with this

import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { getPlan } from '../lib/planFeatures';

// ── FLIP THIS TO SWITCH MODES ─────────────────────────
export const TEST_MODE = false;
// TEST_MODE = true  → instant upgrade, no payment (for testing)
// TEST_MODE = false → real Safepay checkout
// ─────────────────────────────────────────────────────

const PLAN_SLOTS = {
  trial:      1,
  basic:      5,
  pro:        20,
  enterprise: 999,
};

const PLAN_PRICES_USD = {
  basic:      { monthly: 50,   yearly: 480  },
  pro:        { monthly: 120,  yearly: 1152 },
  enterprise: { monthly: 350,  yearly: 3360 },
};

export function useCompanyPlan(company, refreshCompany) {
  const [upgrading,    setUpgrading]    = useState(false);
  const [cancelling,   setCancelling]   = useState(false);
  const [upgradeError, setUpgradeError] = useState('');

  const currentPlan = company?.plan || 'trial';
  const plan        = getPlan(currentPlan);

  const can = {
    postJobs:            plan.jobSlots === -1 || (company?.slots_used || 0) < (plan.jobSlots ?? 1),
    viewApplicants:      currentPlan !== 'trial',
    accessResumes:       currentPlan !== 'trial',
    contactApplicants:   currentPlan !== 'trial',
    exportApplicants:    ['pro','enterprise'].includes(currentPlan),
    featuredJobs:        ['pro','enterprise'].includes(currentPlan),
    viewAnalytics:       ['pro','enterprise'].includes(currentPlan),
    targetByCountry:     ['pro','enterprise'].includes(currentPlan),
    targetByInstitution: ['pro','enterprise'].includes(currentPlan),
    priorityPlacement:   currentPlan === 'enterprise',
  };

  // ── UPGRADE PLAN ─────────────────────────────────────
  const upgradePlan = async (targetPlan, cycle = 'monthly') => {
    if (targetPlan === currentPlan) return { success: false };

    setUpgrading(true);
    setUpgradeError('');

    try {
      // ── TEST MODE: instant upgrade no payment ───────
      if (TEST_MODE) {
        await _applyUpgradeLocally(targetPlan, cycle);
        await refreshCompany();
        setUpgrading(false);
        return { success: true, testMode: true };
      }

      // ── PRODUCTION: call Edge Function then redirect─
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Not authenticated. Please log in again.');

      // Call create-payment Edge Function
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: {
          company_id:    company.id,
          company_name:  company.name   || '',
          contact_email: company.contact_email || '',
          target_plan:   targetPlan,
          billing_cycle: cycle,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (error) throw new Error(error.message || 'Failed to create payment session');
      if (!data?.success) throw new Error(data?.error || 'Payment session creation failed');

      // Save intent to sessionStorage so we can verify after redirect
      sessionStorage.setItem('sp_plan',    targetPlan);
      sessionStorage.setItem('sp_cycle',   cycle);
      sessionStorage.setItem('sp_company', company.id);

      // Redirect to Safepay checkout — page leaves here
      window.location.href = data.checkout_url;

      return { success: true, redirecting: true };

    } catch (err) {
      const msg = err?.message || 'Something went wrong. Please try again.';
      console.error('[Safepay] upgradePlan error:', err);
      setUpgradeError(msg);
      setUpgrading(false);
      return { success: false, error: msg };
    }
  };

  // ── VERIFY PAYMENT (called after Safepay redirect) ───
  const verifyPayment = async (tracker, targetPlan, cycle) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: {
          tracker,
          company_id:    company.id,
          target_plan:   targetPlan,
          billing_cycle: cycle,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (error) throw new Error(error.message || 'Verification failed');
      if (!data?.success) throw new Error(data?.error || 'Payment could not be verified');

      await refreshCompany();

      // Clear sessionStorage
      sessionStorage.removeItem('sp_plan');
      sessionStorage.removeItem('sp_cycle');
      sessionStorage.removeItem('sp_company');

      return { success: true };
    } catch (err) {
      console.error('[Safepay] verifyPayment error:', err);
      return { success: false, error: err.message };
    }
  };

  // ── TEST MODE instant upgrade ─────────────────────────
  const _applyUpgradeLocally = async (targetPlan, cycle) => {
    const now       = new Date();
    const expiresAt = new Date(now);
    const nextBill  = new Date(now);

    if (cycle === 'yearly') {
      expiresAt.setFullYear(expiresAt.getFullYear() + 1);
      nextBill.setFullYear(nextBill.getFullYear() + 1);
    } else {
      expiresAt.setMonth(expiresAt.getMonth() + 1);
      nextBill.setMonth(nextBill.getMonth() + 1);
    }

    const { error } = await supabase.from('companies').update({
      plan:              targetPlan,
      billing_cycle:     cycle,
      plan_started_at:   now.toISOString(),
      plan_expires_at:   expiresAt.toISOString(),
      next_billing_date: nextBill.toISOString(),
      cancelled_at:      null,
      slots_total:       PLAN_SLOTS[targetPlan] ?? 1,
      updated_at:        now.toISOString(),
    }).eq('id', company.id);

    if (error) throw error;

    // Log to billing_history
    await supabase.from('billing_history').insert({
      company_id:    company.id,
      plan:          targetPlan,
      billing_cycle: cycle,
      amount_usd:    0,
      status:        'paid',
      billing_date:  now.toISOString(),
      test_mode:     true,
    }).catch(() => {});
  };

  // ── CANCEL PLAN ──────────────────────────────────────
  const cancelPlan = async () => {
    setCancelling(true);
    try {
      const { error } = await supabase.from('companies').update({
        cancelled_at: new Date().toISOString(),
        updated_at:   new Date().toISOString(),
      }).eq('id', company.id);
      if (error) throw error;
      await refreshCompany();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    } finally {
      setCancelling(false);
    }
  };

  return {
    plan, can, currentPlan,
    upgrading, cancelling, upgradeError,
    upgradePlan, verifyPayment, cancelPlan,
    testMode: TEST_MODE,
  };
}